// Pure helpers for the self-learning access flow (sign-up → identity check →
// free trial → paid base access). No React here so they stay unit-testable;
// the components in `components/access-gate.tsx` / `access-banner.tsx` call
// these with the `access.me` payload.

export type IdentityStatus = 'unverified' | 'verified' | 'needs_review' | 'declined';
export type BaseAccessState = 'not_started' | 'trial' | 'active' | 'expired';

/** The slice of `trpc.access.me` the gate decisions need. */
export type AccessSnapshot = {
  userId: string | null;
  isSelfLearning: boolean;
  identity: { status: IdentityStatus; attempts: number; canRetry: boolean };
  base: { required: boolean; state: BaseAccessState; daysLeft: number | null; trialAvailable: boolean };
  limited: boolean;
  /** `users.status`; self-signups are born 'awaiting-terms' so the terms dialog opens first. */
  termsStatus?: string | null;
};

export type GateMode =
  /** Still waiting for `access.me`. */
  | 'loading'
  /** Logged in with Google but no student record yet → call `access.bootstrap`. */
  | 'bootstrap'
  /** Record exists, terms not accepted yet: the terms dialog (modal) is open on top. */
  | 'terms'
  /** Self-learning student who has not been validated → full-page identity screen. */
  | 'validate'
  /** Validated, never enrolled → the store is the only destination (trial hero). */
  | 'store'
  /** The normal app (possibly in limited mode — see `isLockedPath` / `LimitedVeil`). */
  | 'app';

/** Which screen the student gets, from the access payload and where they are. */
export function decideGate(snapshot: AccessSnapshot | null | undefined, pathname: string): GateMode {
  if (!snapshot) return 'loading';
  if (snapshot.userId === null) return 'bootstrap';
  if (!snapshot.isSelfLearning) return 'app';
  if (snapshot.termsStatus === 'awaiting-terms') return 'terms';
  if (snapshot.identity.status !== 'verified') return 'validate';
  if (snapshot.base.state === 'not_started' && snapshot.base.trialAvailable && !isStorePath(pathname)) {
    return 'store';
  }
  return 'app';
}

export const VALIDATE_PATH = '/validate';
export const STORE_PATH = '/products';

export function isStorePath(pathname: string): boolean {
  return pathname === STORE_PATH || pathname.startsWith(`${STORE_PATH}/`);
}

/**
 * Pages a student in limited mode (trial over, nothing paid) sees faded and
 * inert (`LimitedVeil`). The dashboard, the store and `/validate` stay usable.
 */
export const LOCKED_PATHS = [
  '/journey',
  '/mural',
  '/classes',
  '/calendar',
  '/central',
  '/grammar-aid',
  '/feedback',
  '/onboard',
  '/conquistas',
  '/mellinai',
] as const;

export function isLockedPath(pathname: string): boolean {
  return LOCKED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export type BannerKind = 'none' | 'trial' | 'limited';

/** Which banner the shell shows above every page. */
export function bannerKindOf(snapshot: Pick<AccessSnapshot, 'base' | 'limited'> | null | undefined): BannerKind {
  if (!snapshot || !snapshot.base.required) return 'none';
  if (snapshot.limited) return 'limited';
  if (snapshot.base.state === 'trial') return 'trial';
  return 'none';
}

export type BaseCta =
  /** "Começar grátis por N dias" → `access.startTrial`. */
  | { kind: 'trial' }
  /** In the trial: "N dias grátis restantes · Assinar". */
  | { kind: 'subscribe-trial'; daysLeft: number | null }
  /** Expired, or no trial left: "Assinar". */
  | { kind: 'subscribe' }
  /** Paid and open. */
  | { kind: 'active'; daysLeft: number | null }
  /** Group / private / b2b students: already part of their plan. */
  | { kind: 'included' };

/** The base product's call to action, from the student's base access. */
export function baseCtaOf(base: AccessSnapshot['base']): BaseCta {
  if (!base.required) return { kind: 'included' };
  if (base.trialAvailable) return { kind: 'trial' };
  if (base.state === 'trial') return { kind: 'subscribe-trial', daysLeft: base.daysLeft };
  if (base.state === 'active') return { kind: 'active', daysLeft: base.daysLeft };
  return { kind: 'subscribe' };
}

// -- CPF (the Brazilian tax id ASAAS needs on the payer) --

/** Digits only, capped at 11. */
export function cpfDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 11);
}

/** "000.000.000-00" as the student types. */
export function formatCpf(value: string): string {
  const d = cpfDigits(value);
  const parts = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 9)].filter(Boolean);
  let out = parts.join('.');
  if (d.length > 9) out += `-${d.slice(9)}`;
  return out;
}

/** Mod-11 check digits; repeated-digit sequences (111.111.111-11) are refused. */
export function isValidCpf(value: string): boolean {
  const d = cpfDigits(value);
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false;
  const digit = (len: number) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(d[i]) * (len + 1 - i);
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return digit(9) === Number(d[9]) && digit(10) === Number(d[10]);
}

// -- Where to go after Google sends the student back --

const RETURN_TO_KEY = 'p2e.returnTo';

/** Only same-app paths are honoured (never a full URL from the query string). */
export function sanitizeReturnTo(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  if (value === '/welcome' || value.startsWith('/welcome?')) return null;
  return value;
}

export function rememberReturnTo(storage: Pick<Storage, 'setItem'> | null, value: string | null | undefined): void {
  const safe = sanitizeReturnTo(value);
  if (!safe || !storage) return;
  try {
    storage.setItem(RETURN_TO_KEY, safe);
  } catch {
    // Private mode / blocked storage: the student simply lands on the dashboard.
  }
}

/** Reads and clears the remembered path. */
export function takeReturnTo(storage: Pick<Storage, 'getItem' | 'removeItem'> | null): string | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(RETURN_TO_KEY);
    if (value) storage.removeItem(RETURN_TO_KEY);
    return sanitizeReturnTo(value);
  } catch {
    return null;
  }
}
